import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyAds.css';

const ClientPortal = () => {
    const [clientData, setClientData] = useState(null);
    const [ads, setAds] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [showCreateAd, setShowCreateAd] = useState(false);
    const [showCreativeBrief, setShowCreativeBrief] = useState(false);
    const [newAd, setNewAd] = useState({
        title: '',
        description: '',
        image_url: '',
        needs_design_help: false,
        creative_brief: ''
    });

    useEffect(() => {
        fetchClientData();
        fetchClientAds();
        fetchClientContracts();
    }, []);

    const fetchClientData = async () => {
        try {
            const response = await axios.get('/api/client/profile');
            setClientData(response.data);
        } catch (error) {
            console.error('Error fetching client data:', error);
        }
    };

    const fetchClientAds = async () => {
        try {
            const response = await axios.get('/api/client/ads');
            setAds(response.data);
        } catch (error) {
            console.error('Error fetching client ads:', error);
        }
    };

    const fetchClientContracts = async () => {
        try {
            const response = await axios.get('/api/client/contracts');
            setContracts(response.data);
        } catch (error) {
            console.error('Error fetching client contracts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAd = async (e) => {
        e.preventDefault();
        try {
            // If there's a file to upload, upload it first
            let image_file = newAd.image_file;
            if (newAd.selectedFile) {
                const formData = new FormData();
                formData.append('file', newAd.selectedFile);
                
                try {
                    const uploadResponse = await axios.post('/api/upload', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    
                    if (uploadResponse.status === 200) {
                        image_file = uploadResponse.data.filename;
                    }
                } catch (uploadError) {
                    console.error('Error uploading file:', uploadError);
                    alert('Failed to upload file. Please try again.');
                    return;
                }
            }
            
            // Create the ad with the uploaded filename or URL
            const adData = {
                title: newAd.title,
                description: newAd.description,
                needs_design_help: newAd.needs_design_help,
                creative_brief: newAd.creative_brief
            };
            
            // Add image data based on type
            if (newAd.image_url) {
                adData.image_url = newAd.image_url;
            } else if (image_file) {
                adData.image_file = image_file;
            }
            
            const response = await axios.post('/api/ads', adData);
            
            if (response.status === 201) {
                alert('Advertisement created successfully!');
                setShowCreateAd(false);
                setNewAd({
                    title: '',
                    description: '',
                    image_url: '',
                    image_file: '',
                    selectedFile: null,
                    needs_design_help: false,
                    creative_brief: ''
                });
                fetchClientAds();
            }
        } catch (error) {
            console.error('Error creating ad:', error);
            if (error.response?.status === 401) {
                alert('Please log in to create an advertisement');
            } else {
                alert('Failed to create advertisement');
            }
        }
    };

    const handleDesignRequest = async () => {
        try {
            const response = await axios.post('/api/client/design-request', {
                client_id: clientData.id,
                ad_title: newAd.title,
                creative_brief: newAd.creative_brief,
                service_fee: 500.00
            });
            
            if (response.status === 200) {
                alert('Design request submitted successfully! You will be contacted within 24 hours.');
                setShowCreativeBrief(false);
            }
        } catch (error) {
            console.error('Error submitting design request:', error);
            alert('Failed to submit design request');
        }
    };

    const handleCancelContract = async (contractId) => {
        if (confirm('Are you sure you want to cancel this contract? A 40% cancellation fee will apply.')) {
            try {
                const response = await axios.post(`/api/client/contracts/${contractId}/cancel`);
                alert('Contract cancelled successfully');
                fetchClientContracts();
            } catch (error) {
                console.error('Error cancelling contract:', error);
                alert('Failed to cancel contract');
            }
        }
    };

    if (loading) {
        return <div className="client-portal loading">Loading client portal...</div>;
    }

    return (
        <div className="client-portal">
            <div className="client-header">
                <h1>Client Portal</h1>
                {clientData && (
                    <div className="client-info">
                        <h2>Welcome, {clientData.name}!</h2>
                        <p>Email: {clientData.email}</p>
                        <p>Member since: {new Date(clientData.created_at).toLocaleDateString()}</p>
                    </div>
                )}
            </div>

            <div className="client-tabs">
                <button 
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab ${activeTab === 'ads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ads')}
                >
                    My Ads
                </button>
                <button 
                    className={`tab ${activeTab === 'contracts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('contracts')}
                >
                    My Contracts
                </button>
                <button 
                    className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('billing')}
                >
                    Billing
                </button>
            </div>

            <div className="client-content">
                {activeTab === 'overview' && (
                    <div className="overview">
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Active Ads</h3>
                                <p className="stat-number">{ads.filter(ad => ad.status === 'active').length}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Active Contracts</h3>
                                <p className="stat-number">{contracts.filter(c => c.is_active).length}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Spent</h3>
                                <p className="stat-number">${contracts.reduce((sum, c) => sum + parseFloat(c.amount), 0).toFixed(2)}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Design Requests</h3>
                                <p className="stat-number">{ads.filter(ad => ad.needs_design_help).length}</p>
                            </div>
                        </div>

                        <div className="quick-actions">
                            <h3>Quick Actions</h3>
                            <div className="action-buttons">
                                <button onClick={() => setShowCreateAd(true)} className="btn btn-primary">
                                    Create New Ad
                                </button>
                                <button onClick={() => setShowCreativeBrief(true)} className="btn btn-secondary">
                                    Request Design Help
                                </button>
                                <button className="btn btn-info">View Analytics</button>
                                <button className="btn btn-warning">Contact Support</button>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'ads' && (
                    <div className="ads-section">
                        <div className="section-header">
                            <h3>My Advertisements</h3>
                            <button onClick={() => setShowCreateAd(true)} className="btn btn-primary">
                                Create New Ad
                            </button>
                        </div>
                        
                        {ads.length === 0 ? (
                            <p>No advertisements found. Create your first ad!</p>
                        ) : (
                            <div className="ads-list">
                                {ads.map(ad => (
                                    <div key={ad.id} className="ad-item">
                                        <div className="ad-header">
                                            <h4>{ad.title}</h4>
                                            <span className={`status ${ad.status}`}>{ad.status}</span>
                                        </div>
                                        <p>{ad.description}</p>
                                        {ad.image_url && (
                                            <img src={ad.image_url} alt={ad.title} className="ad-image" />
                                        )}
                                        <div className="ad-actions">
                                            <button className="btn btn-sm btn-primary">Edit</button>
                                            <button className="btn btn-sm btn-danger">Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'contracts' && (
                    <div className="contracts-section">
                        <h3>My Contracts</h3>
                        {contracts.length === 0 ? (
                            <p>No contracts found.</p>
                        ) : (
                            <div className="contracts-list">
                                {contracts.map(contract => (
                                    <div key={contract.id} className="contract-item">
                                        <div className="contract-header">
                                            <h4>Contract #{contract.id}</h4>
                                            <span className={`status ${contract.is_active ? 'active' : 'inactive'}`}>
                                                {contract.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="contract-details">
                                            <p><strong>Amount:</strong> ${contract.amount}</p>
                                            <p><strong>Start Date:</strong> {new Date(contract.start_date).toLocaleDateString()}</p>
                                            <p><strong>End Date:</strong> {new Date(contract.end_date).toLocaleDateString()}</p>
                                            <p><strong>Discount:</strong> {contract.discount_percent}%</p>
                                        </div>
                                        {contract.is_active && (
                                            <div className="contract-actions">
                                                <button 
                                                    onClick={() => handleCancelContract(contract.id)}
                                                    className="btn btn-sm btn-warning"
                                                >
                                                    Cancel Contract
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'billing' && (
                    <div className="billing-section">
                        <h3>Billing & Payments</h3>
                        <div className="billing-info">
                            <p><strong>Payment Method:</strong> Visa ending in ****1234</p>
                            <p><strong>Next Payment:</strong> $150.00 on March 15, 2024</p>
                            <p><strong>Total Spent:</strong> ${contracts.reduce((sum, c) => sum + parseFloat(c.amount), 0).toFixed(2)}</p>
                        </div>
                        <div className="billing-actions">
                            <button className="btn btn-primary">Update Payment Method</button>
                            <button className="btn btn-secondary">View Payment History</button>
                            <button className="btn btn-info">Download Invoice</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Ad Modal */}
            {showCreateAd && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '500px',
                        padding: '20px',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                        position: 'relative',
                        zIndex: 1001
                    }}>
                        <h3 style={{marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '1.5rem'}}>Create New Advertisement</h3>
                        
                        {/* Debug: Show if modal is visible */}
                        <p style={{color: 'red', marginBottom: '10px'}}>DEBUG: Modal is visible!</p>
                        
                        <form onSubmit={handleCreateAd} style={{display: 'block', width: '100%'}}>
                            {/* Title Field */}
                            <div style={{marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '6px', border: '2px solid #007bff'}}>
                                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333'}}>Title:</label>
                                <input
                                    type="text"
                                    value={newAd.title}
                                    onChange={(e) => setNewAd({...newAd, title: e.target.value})}
                                    required
                                    placeholder="Enter ad title"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '2px solid #007bff',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        boxSizing: 'border-box',
                                        background: 'white',
                                        color: '#333'
                                    }}
                                />
                            </div>
                            
                            {/* Description Field */}
                            <div style={{marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '6px', border: '2px solid #28a745'}}>
                                <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333'}}>Description:</label>
                                <textarea
                                    value={newAd.description}
                                    onChange={(e) => setNewAd({...newAd, description: e.target.value})}
                                    required
                                    placeholder="Enter ad description"
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '2px solid #28a745',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        boxSizing: 'border-box',
                                        background: 'white',
                                        color: '#333',
                                        minHeight: '100px',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                            
                            {/* Image Section */}
                            <div style={{marginBottom: '20px'}}>
                                <label style={{display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#333', fontSize: '16px'}}>
                                    📸 Advertisement Image
                                </label>
                                
                                {/* Simple File Upload */}
                                <div style={{
                                    border: '3px dashed #007bff',
                                    borderRadius: '12px',
                                    padding: '30px 20px',
                                    textAlign: 'center',
                                    background: '#f8f9fa',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    marginBottom: '15px'
                                }} onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.onchange = (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setNewAd({...newAd, image_file: file.name, selectedFile: file});
                                        }
                                    };
                                    input.click();
                                }}>
                                    
                                    {!newAd.image_file ? (
                                        <div>
                                            <div style={{fontSize: '48px', marginBottom: '15px'}}>📁</div>
                                            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#007bff', marginBottom: '8px'}}>
                                                Click to upload image
                                            </div>
                                            <div style={{fontSize: '14px', color: '#666', marginBottom: '10px'}}>
                                                Drag and drop your image here, or click to browse
                                            </div>
                                            <div style={{fontSize: '12px', color: '#999'}}>
                                                Max size: 50MB • JPG, PNG, GIF, WebP
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{fontSize: '48px', marginBottom: '15px'}}>✅</div>
                                            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#28a745', marginBottom: '8px'}}>
                                                {newAd.image_file}
                                            </div>
                                            <div style={{fontSize: '14px', color: '#666'}}>
                                                Click to change file
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Remove File Button */}
                                {newAd.image_file && (
                                    <button
                                        type="button"
                                        onClick={() => setNewAd({...newAd, image_file: '', selectedFile: null})}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            background: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            marginBottom: '15px'
                                        }}
                                    >
                                        🗑️ Remove File
                                    </button>
                                )}
                                
                                {/* Alternative: Image URL */}
                                <div style={{
                                    padding: '15px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <div style={{fontSize: '14px', color: '#666', marginBottom: '10px', fontWeight: 'bold'}}>
                                        💡 Or use an image URL instead:
                                    </div>
                                    <input
                                        type="url"
                                        placeholder="https://example.com/image.jpg"
                                        value={newAd.image_url}
                                        onChange={(e) => setNewAd({...newAd, image_url: e.target.value})}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>
                            
                            {/* Design Help Checkbox */}
                            <div style={{marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '6px', border: '2px solid #dc3545'}}>
                                <label style={{display: 'flex', alignItems: 'center', marginBottom: '8px', fontWeight: 'bold', color: '#333', cursor: 'pointer'}}>
                                    <input
                                        type="checkbox"
                                        checked={newAd.needs_design_help}
                                        onChange={(e) => setNewAd({...newAd, needs_design_help: e.target.checked})}
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            marginRight: '10px',
                                            display: 'inline-block'
                                        }}
                                    />
                                    Need design help? ($500 additional fee)
                                </label>
                            </div>
                            
                            {/* Buttons */}
                            <div style={{display: 'flex', gap: '10px', marginTop: '20px', padding: '20px', borderTop: '2px solid #dee2e6'}}>
                                <button type="submit" style={{
                                    flex: 2,
                                    padding: '18px 24px',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                    color: 'white',
                                    boxShadow: '0 6px 20px rgba(40, 167, 69, 0.4)',
                                    transition: 'all 0.3s ease'
                                }}>🚀 Create Advertisement</button>
                                <button type="button" onClick={() => setShowCreateAd(false)} style={{
                                    flex: 1,
                                    padding: '18px 24px',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    background: '#6c757d',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Creative Brief Modal */}
            {showCreativeBrief && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Design Service Request</h3>
                        <p>Please provide a creative brief for your advertisement design. Our team will contact you within 24 hours.</p>
                        <form onSubmit={handleDesignRequest}>
                            <div className="form-group">
                                <label>Ad Title:</label>
                                <input
                                    type="text"
                                    value={newAd.title}
                                    onChange={(e) => setNewAd({...newAd, title: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Creative Brief:</label>
                                <textarea
                                    value={newAd.creative_brief}
                                    onChange={(e) => setNewAd({...newAd, creative_brief: e.target.value})}
                                    placeholder="Describe your vision, target audience, brand guidelines, etc."
                                    required
                                    rows="6"
                                />
                            </div>
                            <div className="form-group">
                                <p><strong>Service Fee:</strong> $500.00</p>
                            </div>
                            <div className="modal-actions">
                                <button type="submit" className="btn btn-primary">Submit Request</button>
                                <button type="button" onClick={() => setShowCreativeBrief(false)} className="btn btn-secondary">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientPortal;